import {
  OwcGrid,
  OwcTypography,
  OwcProgressSpinner,
} from "@one/react";
import { useEffect, useState } from "react";
import { chunkArray } from "../../../utils/helpers/text";
import { withApollo } from "react-apollo";
import { BOOKING_GET_CHECKLISTS_FOR_EQUIPMENTS } from "../../../gql/bookingapi";
import ChecklistFolder from "../../../icons/ChecklistFolder";

const REPOSITORY_URL = process.env.REACT_APP_REPOSITORY_URL || "";

const EquipmentDetails = ({ show = true, equipmentData, chunkSize = 6, client }) => {
  const [checklistMap, setChecklistMap] = useState({});
  const [loading, setLoading] = useState(false);

  const bookedEquipments = equipmentData?.bookedEquipments || [];
  const columns = chunkArray(bookedEquipments, chunkSize);

  const truncateText = (text, maxLength = 46) =>
    text && text.length > maxLength ? `${text.substring(0, maxLength - 3)}` : text;

  useEffect(() => {
    if (!equipmentData?.bookingEntries?.length) {
      return;
    }

    const keys = [
      ...new Set(
        equipmentData.bookingEntries
          .map((entry) => {
            const equipment = entry.equipment;
            if (!equipment?.siteName || !equipment?.belongingToGroup || !equipment?.equipmentModel) {
              return null;
            }
            return `${equipment.siteName}#${equipment.belongingToGroup}#${equipment.equipmentModel}`;
          })
          .filter(Boolean)
      ),
    ];

    if (!keys.length) {
      return;
    }

    setLoading(true);

    client
      .query({
        query: BOOKING_GET_CHECKLISTS_FOR_EQUIPMENTS,
        variables: { keys },
        fetchPolicy: "no-cache",
      })
      .then((res) => {
        const entries = res?.data?.getChecklistsForEquipments || [];
        const map = Object.fromEntries(entries.map((entry) => [entry.key, entry.checklists]));
        setChecklistMap(map);
      })
      .catch((error) => console.error("Checklist query failed:", error))
      .finally(() => setLoading(false));
  }, [equipmentData, client]);

  if (!show) {
    return null;
  }

  const handleViewDocumentation = (inventoryId) => {
    if (REPOSITORY_URL) {
      window.open(`${REPOSITORY_URL}/equipmentList/${inventoryId}?action=documentation`, "_blank");
    }
  };

  return (
    <>
      <OwcTypography variant="button" style={{ margin: "1.125rem 0" }}>
        Equipment details
      </OwcTypography>
      <OwcTypography variant="caption" style={{ marginBottom: "6px" }}>Documentation and Short ID(s)</OwcTypography>

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "120px",
          }}
        >
          <OwcProgressSpinner diameter={60} />
        </div>
      ) : (
        <OwcGrid
          container
          columns={{
            xs: 1,
            s: 1,
            m: 2,
            l: 3,
            xl: 3,
          }}
        >
          {columns.map((column, index) => (
            <OwcGrid
              item
              key={index}
              xl={1}
              l={1}
              m={1}
              xs={1}
              s={1}
              style={{ marginBottom: 12, alignSelf: "start" }}
            >
              {column.map((shortId, itemIndex) => {
                const entry = equipmentData.bookingEntries.find(
                  (bookingEntry) => bookingEntry.equipment.shortIdentifier === shortId
                );
                const equipment = entry?.equipment;
                const equipmentKey = equipment && `${equipment.siteName}#${equipment.belongingToGroup}#${equipment.equipmentModel}`;
                const checklists = equipmentKey ? checklistMap[equipmentKey] || [] : [];
                const inventoryId = equipment?.inventoryId || equipment?.id;

                return (
                  <div
                    key={itemIndex}
                    style={{
                      marginBottom: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {checklists.length === 0 ? (
                      <OwcTypography
                        variant="button"
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          lineHeight: "140%",
                          letterSpacing: "0%",
                          display: "inline-flex",
                          alignItems: "center",
                          verticalAlign: "middle",
                          fontFamily: "inherit",
                        }}
                      >
                        {truncateText(shortId, 50)}
                      </OwcTypography>
                    ) : (
                      <button
                        onClick={() => handleViewDocumentation(inventoryId)}
                        title={`View documentation for ${shortId}`}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          fontWeight: 600,
                          fontFamily: "inherit",
                          fontSize: 14,
                          lineHeight: "140%",
                          letterSpacing: "0%",
                          display: "inline-flex",
                          alignItems: "center",
                          verticalAlign: "middle",
                          color: "#312f2e"
                        }}
                      >
                        <ChecklistFolder size={18} />
                        &nbsp;{truncateText(shortId)}
                      </button>
                    )}
                  </div>
                );
              })}
            </OwcGrid>
          ))}
        </OwcGrid>
      )}
    </>
  );
};

export default withApollo(EquipmentDetails);
